import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Plus, Copy, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Type, Image as ImageIcon, Square, Files,
} from 'lucide-react';

const ELEMENT_ICON = { text: Type, image: ImageIcon, shape: Square };

/**
 * "Layers & Pages" sidebar category — object ordering/visibility for the
 * active page, plus carousel/Story page management when the asset type
 * supports more than one page. The always-visible bottom strip (in
 * ImagesAndPosts.jsx) shows the same pages/layers at a glance; this panel
 * is where reordering/duplicating/removing actually happens.
 */
export function DesignLayersPages({ pages, activePageId, onSelectPage, onAddPage, onDuplicatePage, onRemovePage, onReorderPage, elements, onSelectElement, selectedElementId, onReorderElement, onRemoveElement, onToggleVisibility, multiPage }) {
  return (
    <div className="space-y-4" data-testid="design-layers-pages-panel">
      {multiPage && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[11px] uppercase tracking-wide text-[#555E6E] flex items-center gap-1"><Files className="h-3 w-3" />Pages ({pages.length})</p>
            <Button size="sm" variant="outline" className="h-6 w-6 p-0 border-white/10" onClick={onAddPage} data-testid="design-add-page-button"><Plus className="h-3 w-3" /></Button>
          </div>
          <div className="space-y-1">
            {pages.map((p, idx) => (
              <div key={p.id} className={`flex items-center gap-1.5 p-1.5 rounded-md border ${p.id === activePageId ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`} data-testid={`design-page-row-${p.id}`}>
                <button onClick={() => onSelectPage(p.id)} className="flex-1 min-w-0 text-left text-xs text-white truncate">Page {idx + 1} ({p.elements.length} objects)</button>
                <button onClick={() => onReorderPage(p.id, -1)} disabled={idx === 0} className="disabled:opacity-20 text-[#8A93A6] hover:text-white" data-testid={`design-page-up-${p.id}`}><ArrowUp className="h-3 w-3" /></button>
                <button onClick={() => onReorderPage(p.id, 1)} disabled={idx === pages.length - 1} className="disabled:opacity-20 text-[#8A93A6] hover:text-white" data-testid={`design-page-down-${p.id}`}><ArrowDown className="h-3 w-3" /></button>
                <button onClick={() => onDuplicatePage(p.id)} className="text-[#8A93A6] hover:text-white" data-testid={`design-page-duplicate-${p.id}`}><Copy className="h-3 w-3" /></button>
                <button onClick={() => onRemovePage(p.id)} disabled={pages.length <= 1} className="disabled:opacity-20 text-red-400 hover:text-red-300" data-testid={`design-page-remove-${p.id}`}><Trash2 className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1.5">Layers ({elements.length})</p>
        {elements.length === 0 ? (
          <p className="text-xs text-[#8A93A6] text-center py-4" data-testid="design-layers-empty">No objects on this page yet.</p>
        ) : (
          <div className="space-y-1">
            {[...elements].reverse().map((el) => {
              const Icon = ELEMENT_ICON[el.type] || Square;
              const realIdx = elements.findIndex((e) => e.id === el.id);
              return (
                <div key={el.id} className={`flex items-center gap-1.5 p-1.5 rounded-md border ${el.id === selectedElementId ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`} data-testid={`design-layer-row-${el.id}`}>
                  <Icon className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
                  <button onClick={() => onSelectElement(el.id)} className="flex-1 min-w-0 text-left text-xs text-white truncate">{el.type === 'text' ? (el.text || 'Text') : el.type}</button>
                  <button onClick={() => onToggleVisibility(el.id)} className="text-[#8A93A6] hover:text-white" data-testid={`design-layer-visibility-${el.id}`}>{el.visible === false ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}</button>
                  <button onClick={() => onReorderElement(el.id, 1)} disabled={realIdx === elements.length - 1} className="disabled:opacity-20 text-[#8A93A6] hover:text-white" data-testid={`design-layer-forward-${el.id}`}><ArrowUp className="h-3 w-3" /></button>
                  <button onClick={() => onReorderElement(el.id, -1)} disabled={realIdx === 0} className="disabled:opacity-20 text-[#8A93A6] hover:text-white" data-testid={`design-layer-backward-${el.id}`}><ArrowDown className="h-3 w-3" /></button>
                  <button onClick={() => onRemoveElement(el.id)} className="text-red-400 hover:text-red-300" data-testid={`design-layer-remove-${el.id}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
